import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import TrustBlock from '../components/common/TrustBlock';
import ReviewsWidget from '../components/common/ReviewsWidget';
import TimeSlotSelector from '../components/common/TimeSlotSelector';
import DeliverySelector from '../components/common/DeliverySelector';
import ZoneChecker from '../components/common/ZoneChecker';
import MobileStickyBar from '../components/common/MobileStickyBar';
import CallButton from '../components/common/CallButton';
import BookingInfoBlock from '../components/common/BookingInfoBlock';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { PROFILE_DISCOUNTS, getDiscountInfo } from '../config/discounts';
import { calculateFullPrice, applyWeekdayDiscount } from '../config/pricingRules';
import { toast } from 'sonner';
import { formatDate } from '../utils/dateUtils';
import {
  Minus, Plus, Trash2, ShoppingBag, Sparkles,
  Clock, ArrowRight, Shield, Check, Tag, Percent, Wallet, Gift, Truck, AlertCircle
} from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PanierPage() {
  const { user } = useAuth();
  const [walletBalance, setWalletBalance] = useState(0);

  // Charger wallet parrainage
  // eslint-disable-next-line
  useEffect(() => {
    if (user?.id) {
      fetch(`${BACKEND}/api/parrainage/wallet/${user.id}`)
        .then(r => r.json())
        .then(d => setWalletBalance(d.balance || 0))
        .catch(() => {});
    }
  }, [user?.id]);
  const {
    cartItems, updateQuantity, removeItem, addItem,
    getTotal, clientProfile, setClientProfile,
    updateItemDate, getAlertesDateCart
  } = useCart();
  const [upsells, setUpsells] = useState([]);
  const [loadingUpsells, setLoadingUpsells] = useState(false);
  const [associes, setAssocies] = useState([]);
  const [deliveryResult, setDeliveryResult] = useState({ tarif_total: 0, option_label: 'À définir' });
  const [detectedZoneId, setDetectedZoneId] = useState(null);
  const [deliverySlot, setDeliverySlot] = useState('apres_midi');
  const [returnSlot, setReturnSlot] = useState('retour_10h');
  const [creatingQuote, setCreatingQuote] = useState(false);
  // Infos client — pré-rempli si connecté
  const [clientInfo, setClientInfo] = useState({ nom: '', email: '', telephone: '' });
  // Adresse livraison : lieu de l'événement (GPS / mission)
  const [adresseLivraison, setAdresseLivraison] = useState('');
  const [adresseSameChezmoi, setAdresseSameChezmoi] = useState(false);
  // Adresse facturation : domicile du payeur (contrat & facture) — optionnel
  const [adresseFacturation, setAdresseFacturation] = useState('');
  const [facturationDifferente, setFacturationDifferente] = useState(false);
  const [paiementMode, setPaiementMode] = useState('acompte');
  const navigate = useNavigate();

  // Pré-remplir depuis le profil connecté + verrouiller le profil tarifaire
  useEffect(() => {
    if (user) {
      // Forcer le profil tarifaire selon le compte (non modifiable manuellement)
      const profileFromAccount = user.profile || 'particulier';
      setClientProfile(profileFromAccount);
      setClientInfo(prev => ({
        ...prev,
        nom: user.nom ? `${user.prenom || ''} ${user.nom}`.trim() : prev.nom,
        email: user.email || prev.email,
        telephone: user.telephone || prev.telephone,
      }));
    } else {
      // Non connecté → toujours tarif particulier
      setClientProfile('particulier');
    }
  }, [user, setClientProfile]);

  const fetchUpsells = useCallback(async () => {
    if (!cartItems[0]?.product_id) return;
    setLoadingUpsells(true);
    try {
      const res = await fetch(`${BACKEND}/api/packs/upsell/${cartItems[0].product_id}`);
      const data = await res.json();
      const cartIds = cartItems.map(i => i.product_id);
      setUpsells((data.suggestions || []).filter(s => !cartIds.includes(s.id)).slice(0, 4));
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
    setLoadingUpsells(false);
  }, [cartItems]);

  useEffect(() => {
    if (cartItems.length > 0) fetchUpsells();
  }, [cartItems, fetchUpsells]);

  // Produits à vendre associés aux articles du panier (prestations/locations)
  const fetchAssocies = useCallback(async () => {
    const sourceIds = cartItems.map(i => i.product_id).filter(Boolean);
    if (sourceIds.length === 0) { setAssocies([]); return; }
    try {
      const results = await Promise.all(sourceIds.map(pid =>
        fetch(`${BACKEND}/api/products/${pid}/associes`).then(r => r.json()).catch(() => ({ produits: [] }))
      ));
      const map = {};
      results.forEach(res => (res.produits || []).forEach(p => { map[p.id] = p; }));
      const cartIds = new Set(cartItems.map(i => i.product_id));
      setAssocies(Object.values(map).filter(p => !cartIds.has(p.id)));
    } catch (err) { setAssocies([]); }
  }, [cartItems]);

  useEffect(() => {
    if (cartItems.length > 0) fetchAssocies();
    else setAssocies([]);
  }, [cartItems, fetchAssocies]);

  async function createQuote() {
    if (cartItems.length === 0) return;
    if (!clientInfo.email) { toast.error('Votre email est requis'); return; }
    if (!clientInfo.telephone) { toast.error('Votre téléphone est requis'); return; }

    // Adresse de livraison : utiliser l'adresse événement saisie, ou l'adresse personnelle si cochée
    const adresseLivraisonFinal = adresseSameChezmoi
      ? (user?.adresse_personnelle || '')
      : adresseLivraison;
    if (!adresseLivraisonFinal.trim()) {
      toast.error("L'adresse de livraison de l'événement est requise");
      return;
    }

    // Vérifier dates des items location
    const locationItems = cartItems.filter(i => i.product?.type === 'location');
    const itemsSansDate = locationItems.filter(i => !i.date_location);
    if (itemsSansDate.length > 0) {
      toast.error(`Date manquante pour : ${itemsSansDate[0].product?.nom?.slice(0, 30)}`);
      return;
    }

    setCreatingQuote(true);
    try {
      const produits = cartItems.map(i => ({
        product_id: i.product_id,
        quantite: i.quantite,
        date_location: i.date_location || null,
        option_livraison: i.option_livraison || null,
      }));
      const totalTransport = deliveryResult?.tarif_total || 0;
      const montantPaiement = paiementMode === 'total' ? (subtotal + totalTransport) : Math.round((subtotal + totalTransport) * 0.30 * 100) / 100;
      const premiereDateLocation = locationItems[0]?.date_location || null;

      const res = await fetch(`${BACKEND}/api/devis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session: {
            produits,
            client_profile: clientProfile,
            nb_adultes: 20,
            nb_enfants: 0,
            date_evenement: premiereDateLocation,
            adresse: adresseLivraisonFinal,
            notes: `Transport: ${deliveryResult?.option_label || ''} | Livraison: ${deliverySlot} | Reprise: ${returnSlot} | Paiement: ${paiementMode}`,
          },
          client_profile: clientProfile,
          client_nom: clientInfo.nom,
          client_email: clientInfo.email,
          client_telephone: clientInfo.telephone,
          adresse_livraison: adresseLivraisonFinal,
          adresse_facturation: facturationDifferente ? adresseFacturation : null,
          mode_paiement: paiementMode,
          montant_paiement_initial: montantPaiement,
        })
      });
      const quote = await res.json();
      toast.success('Devis créé !', { description: `Réf. ${quote.numero}` });
      navigate(`/devis/${quote.id}`);
    } catch (e) {
      toast.error('Erreur lors de la création du devis');
    } finally {
      setCreatingQuote(false);
    }
  }

  const subtotal = getTotal();
  const transport = deliveryResult?.tarif_total || 0;
  const total = subtotal + transport;
  const acompte = Math.round(total * 0.30 * 100) / 100;
  const solde = Math.round(total * 0.70 * 100) / 100;

  // ── Règle < 7 jours → paiement total obligatoire ─────────────────────────
  const hasItemUnder7Days = cartItems.some(item => {
    if (item.product?.type !== 'location' || !item.date_location) return false;
    const diffDays = Math.floor((new Date(item.date_location) - new Date()) / (1000 * 60 * 60 * 24));
    return diffDays < 7;
  });

  // Forcer paiement total si < 7j
  React.useEffect(() => {
    if (hasItemUnder7Days) setPaiementMode('total');
  }, [hasItemUnder7Days]);

  // Calcul économies totales profil
  const profileCfg = PROFILE_DISCOUNTS[clientProfile];
  const totalSavings = clientProfile !== 'particulier' && profileCfg?.pct > 0
    ? cartItems.reduce((acc, item) => {
        const publicPrix = item.product?.tarifs?.particulier || 0;
        const profilePrix = item.product?.tarifs?.[clientProfile] || publicPrix;
        return acc + (publicPrix - profilePrix) * item.quantite;
      }, 0)
    : 0;

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />

      <div className="container-ose" style={{ paddingTop: '2rem', paddingBottom: '4rem' }}>
        <h1 className="font-display text-2xl sm:text-3xl font-bold mb-2"
          style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
          Mon panier
        </h1>

        {cartItems.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingBag size={52} className="mx-auto mb-4" style={{ color: 'var(--border)' }} />
            <p className="font-semibold text-base mb-2" style={{ color: 'var(--text-muted)' }}>Votre panier est vide</p>
            <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>Découvrez nos animations et composez votre événement</p>
            <Link to="/creer-evenement" className="btn-teal px-7 py-3 rounded-xl text-sm font-bold inline-flex items-center gap-2">
              Créer mon événement <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-5 gap-6">
            {/* Left — Items + options */}
            <div className="lg:col-span-3 space-y-4">

              {/* Items */}
              <div className="rounded-2xl overflow-hidden" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                <div className="px-5 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)' }}>
                  <p className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>
                    {cartItems.length} animation{cartItems.length > 1 ? 's' : ''}
                  </p>
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Sous-total : <strong className="price-gold">{subtotal.toFixed(2)}€</strong></span>
                </div>

                {cartItems.map((item, idx) => {
                  const tarifs = item.product?.tarifs || {};
                  const publicPrix = tarifs.particulier || 0;
                  const prix = tarifs[clientProfile] || publicPrix;
                  const hasDiscount = clientProfile !== 'particulier' && prix < publicPrix;
                  const discountInfo = hasDiscount ? getDiscountInfo(clientProfile, publicPrix, prix) : null;
                  return (
                    <div key={item.product_id}
                      className="flex items-center gap-4 px-5 py-4"
                      style={{ borderBottom: idx < cartItems.length - 1 ? '1px solid var(--border)' : 'none' }}>

                      {/* Image */}
                      <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0"
                        style={{ border: '1px solid var(--border)' }}>
                        <img
                          src={item.product?.images?.[0] || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=160&q=70'}
                          alt={item.product?.nom}
                          className="w-full h-full object-cover"
                        loading="lazy" />
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm mb-0.5" style={{ color: 'var(--ink)' }}>{item.product?.nom}</p>
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                            {item.product?.type === 'location' ? 'Location' : 'Vente boutique'}
                          </p>
                          {hasDiscount && discountInfo && (
                            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-bold"
                              style={{ background: discountInfo.bg, color: discountInfo.color }}>
                              <Tag size={10} />-{discountInfo.pct}%
                            </span>
                          )}
                        </div>

                        {/* DATE DE LOCATION */}
                        {item.product?.type === 'location' && (
                          <div className="mb-2">
                            {item.date_location ? (
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold"
                                  style={{ background: 'rgba(31,157,122,0.1)', color: '#1F9D7A' }}>
                                  <Clock size={10} />
                                  📅 {formatDate(item.date_location)}
                                </span>
                                <button
                                  onClick={() => updateItemDate(item.product_id, null, null)}
                                  className="text-xs underline"
                                  style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>
                                  Changer
                                </button>
                                {/* Alerte 7j */}
                                {(() => {
                                  const diff = Math.floor((new Date(item.date_location) - new Date()) / (1000 * 60 * 60 * 24));
                                  if (diff < 7) return (
                                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                                      style={{ background: 'rgba(239,68,68,0.1)', color: '#EF4444' }}>
                                      ⚠️ {diff}j avant — paiement requis
                                    </span>
                                  );
                                  return null;
                                })()}
                              </div>
                            ) : (
                              <div>
                                <label className="block text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>
                                  📅 Date de location *
                                </label>
                                <input
                                  type="date"
                                  min={new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10)}
                                  onChange={e => updateItemDate(item.product_id, e.target.value, null)}
                                  className="px-2 py-1.5 rounded-lg text-xs"
                                  style={{ border: '1.5px solid #F59E0B', background: 'rgba(245,158,11,0.06)', color: 'var(--ink)', outline: 'none' }}
                                />
                                <p className="text-xs mt-0.5" style={{ color: '#F59E0B' }}>⚠️ Date obligatoire pour la réservation</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Qty + Prix */}
                        <div className="flex items-center gap-2 flex-wrap">
                          {(() => {
                            const isLocation = item.product?.type === 'location';
                            const maxQty = isLocation ? (item.product?.nb_unites || 1) : 99;
                            const atMax = item.quantite >= maxQty;
                            return (
                              <div className="flex items-center gap-0.5">
                                <div className="flex items-center gap-1.5 rounded-xl px-1"
                                  style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                                  <button onClick={() => updateQuantity(item.product_id, item.quantite - 1)}
                                    className="w-7 h-7 flex items-center justify-center rounded-lg"
                                    style={{ color: 'var(--text-muted)' }}><Minus size={13} /></button>
                                  <span className="font-bold text-sm w-6 text-center" style={{ color: 'var(--ink)' }}>{item.quantite}</span>
                                  <button
                                    onClick={() => !atMax && updateQuantity(item.product_id, item.quantite + 1)}
                                    disabled={atMax}
                                    title={atMax ? `1 unité disponible` : undefined}
                                    className="w-7 h-7 flex items-center justify-center rounded-lg transition-opacity"
                                    style={{
                                      color: atMax ? 'var(--border)' : 'var(--ink)',
                                      cursor: atMax ? 'not-allowed' : 'pointer',
                                      opacity: atMax ? 0.4 : 1,
                                    }}><Plus size={13} /></button>
                                </div>
                                {isLocation && maxQty === 1 && (
                                  <span className="text-xs ml-1.5 px-1.5 py-0.5 rounded-lg"
                                    style={{ background: 'rgba(93,188,191,0.1)', color: '#0B4B4E', fontSize: '10px' }}>
                                    1 unité dispo
                                  </span>
                                )}
                                {isLocation && maxQty > 1 && (
                                  <span className="text-xs ml-1.5" style={{ color: 'var(--text-muted)', fontSize: '10px' }}>
                                    max {maxQty}
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                          <div className="flex items-center gap-1.5">
                            {hasDiscount && (
                              <span className="text-xs line-through" style={{ color: 'var(--text-muted)' }}>
                                {(publicPrix * item.quantite).toFixed(2)}€
                              </span>
                            )}
                            <span className="price-gold font-bold text-base">{(prix * item.quantite).toFixed(2)}€</span>
                          </div>
                          {hasDiscount && discountInfo && (
                            <span className="text-xs font-medium" style={{ color: discountInfo.color }}>
                              -{(discountInfo.saving * item.quantite).toFixed(2)}€
                            </span>
                          )}
                        </div>
                      </div>

                      <button onClick={() => removeItem(item.product_id)}
                        className="p-2 rounded-xl transition-colors flex-shrink-0"
                        style={{ color: '#D94B5D', background: 'rgba(217,75,93,0.06)' }}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* ── Bloc infos tarifaires (accordéon compact) ── */}
              {cartItems.some(i => i.product?.type === 'location') && (
                <BookingInfoBlock compact showTitle />
              )}

              {/* Profil tarifaire — lecture seule selon compte */}
              {user ? (
                /* ── CONNECTÉ : profil verrouillé depuis l'inscription ── */
                <div className="p-4 rounded-2xl" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                  <p className="text-sm font-semibold mb-2" style={{ color: 'var(--ink)' }}>Votre profil tarifaire</p>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl"
                      style={{ background: profileCfg?.bg || 'var(--teal-mist)', border: `1px solid ${profileCfg?.color || 'var(--teal)'}30` }}>
                      <span className="text-sm font-bold" style={{ color: profileCfg?.color || '#0B4B4E' }}>
                        {profileCfg?.label || 'Particulier'}
                      </span>
                      {profileCfg?.pct > 0 && (
                        <span className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full"
                          style={{ background: profileCfg.color, color: '#fff' }}>
                          <Percent size={10} />-{profileCfg.pct}%
                        </span>
                      )}
                    </div>
                    <p className="text-xs" style={{ color: 'var(--text-muted)', lineHeight: 1.5 }}>
                      {profileCfg?.pct > 0
                        ? `Remise ${profileCfg.label} appliquée automatiquement sur votre devis`
                        : 'Tarif standard appliqué à votre devis'}
                    </p>
                  </div>
                </div>
              ) : (
                /* ── NON CONNECTÉ : tarif particulier + invitation à s'inscrire ── */
                <div className="p-4 rounded-2xl" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                  <p className="text-sm font-semibold mb-2" style={{ color: 'var(--ink)' }}>Votre profil tarifaire</p>
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-xl mb-3"
                    style={{ background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.3)' }}>
                    <span className="text-sm font-bold" style={{ color: '#0B4B4E' }}>Particulier</span>
                    <span className="text-xs ml-1" style={{ color: 'rgba(11,75,78,0.6)' }}>— tarif standard</span>
                  </div>
                  <div className="flex items-start gap-2 p-3 rounded-xl"
                    style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.2)' }}>
                    <Gift size={14} style={{ color: '#C58B19', flexShrink: 0, marginTop: 1 }} />
                    <p className="text-xs" style={{ color: '#C58B19', lineHeight: 1.6 }}>
                      <strong>Vous êtes une entreprise, association ou CE ?</strong><br />
                      <Link to="/inscription" style={{ color: 'var(--teal)', fontWeight: 700, textDecoration: 'underline' }}>
                        Créez votre compte
                      </Link>{' '}ou{' '}
                      <Link to="/connexion" style={{ color: 'var(--teal)', fontWeight: 700, textDecoration: 'underline' }}>
                        connectez-vous
                      </Link>{' '}
                      pour bénéficier de <strong>-10% à -15%</strong> selon votre profil.
                    </p>
                  </div>
                </div>
              )}

              {/* Delivery + Time slots */}
              <div className="p-5 rounded-2xl space-y-5" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                <div className="mb-4">
                <ZoneChecker
                  compact
                  showSeoPhrase
                  onZoneDetected={(data) => {
                    if (data.zone?.tarif_livraison !== null && data.zone?.tarif_livraison !== undefined) {
                      // Mettre à jour le tarif ET synchroniser la zone dans DeliverySelector
                      setDeliveryResult(prev => ({
                        ...prev,
                        tarif_total: (data.zone.tarif_livraison || 0) + (data.zone.tarif_reprise || 0),
                        zone_label: data.zone.label,
                        option_label: 'Livraison + Reprise'
                      }));
                      // Détecter la zone_id correspondante
                      const zoneMap = { 0: 'zone_1', 1: 'zone_1', 2: 'zone_2', 3: 'zone_3', 4: 'zone_hors' };
                      const zoneLevel = data.zone?.niveau !== undefined ? data.zone.niveau : null;
                      if (zoneLevel !== null) setDetectedZoneId(zoneMap[zoneLevel] || 'zone_1');
                    }
                  }}
                />
              </div>
              <DeliverySelector
                onDeliveryChange={setDeliveryResult}
                initialOption="livraison_reprise"
                externalZone={detectedZoneId}
                nbArticles={cartItems.length}
              />
                <div className="divider-gold" style={{ opacity: 0.3 }} />
                <TimeSlotSelector type="livraison" selected={deliverySlot} onSelect={setDeliverySlot} />
                <div className="divider-gold" style={{ opacity: 0.3 }} />
                <TimeSlotSelector type="reprise" selected={returnSlot} onSelect={setReturnSlot} />
              </div>

              {/* "Indispensable avec ce produit" */}
              {upsells.length > 0 && (
                <div className="p-5 rounded-2xl" style={{ background: '#FFFFFF', border: '1.5px solid rgba(93,188,191,0.3)' }}>
                  <div className="flex items-center gap-2 mb-4">
                    <Sparkles size={16} style={{ color: 'var(--teal)' }} />
                    <p className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>Indispensable avec ce produit</p>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {upsells.map(s => (
                      <Link key={s.id} to={`/produit/${s.slug || s.id}`}
                        className="card-premium overflow-hidden block group">
                        <img src={s.images?.[0] || ''} alt={s.nom}
                          className="w-full h-20 object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                        <div className="p-2">
                          <p className="text-xs font-semibold line-clamp-2 mb-0.5" style={{ color: 'var(--ink)' }}>{s.nom}</p>
                          <p className="price-gold text-xs">{s.tarifs?.particulier}€</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* "À ajouter à votre location" — produits à vendre associés */}
              {associes.length > 0 && (
                <div className="p-5 rounded-2xl" data-testid="panier-associes-section"
                  style={{ background: '#FFFFFF', border: '1.5px solid rgba(212,175,55,0.3)' }}>
                  <div className="flex items-center gap-2 mb-1">
                    <ShoppingBag size={16} style={{ color: '#C58B19' }} />
                    <p className="font-semibold text-sm" style={{ color: 'var(--ink)' }}>À ajouter à votre location</p>
                  </div>
                  <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
                    Des produits à acheter pour compléter votre événement
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {associes.map(a => (
                      <div key={a.id} className="card-premium overflow-hidden flex flex-col"
                        data-testid={`panier-associe-${a.id}`}>
                        <Link to={`/produit/${a.slug || a.id}`}>
                          <img src={a.images?.[0] || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=160&q=70'}
                            alt={a.nom}
                            className="w-full h-20 object-cover hover:scale-105 transition-transform duration-300" loading="lazy" />
                        </Link>
                        <div className="p-2 flex-1 flex flex-col">
                          <p className="text-xs font-semibold line-clamp-2 mb-0.5" style={{ color: 'var(--ink)' }}>{a.nom}</p>
                          <p className="price-gold text-xs mb-2">{a.tarifs?.particulier || 0}€</p>
                          <button
                            onClick={() => { addItem({ ...a, quantite: 1 }); toast.success(`${a.nom} ajouté`); }}
                            data-testid={`panier-add-associe-${a.id}`}
                            className="mt-auto text-xs font-semibold py-1.5 rounded-lg flex items-center justify-center gap-1"
                            style={{ background: 'var(--teal-mist)', color: '#0B4B4E' }}>
                            <Plus size={12} /> Ajouter
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Client info */}
              <div className="p-5 rounded-2xl" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                <p className="font-semibold text-sm mb-3" style={{ color: 'var(--ink)' }}>Vos coordonnées</p>
                <div className="grid sm:grid-cols-3 gap-3 mb-4">
                  {[
                    { key: 'nom', label: 'Nom & prénom', placeholder: 'Jean Dupont' },
                    { key: 'email', label: 'Email *', placeholder: 'jean@exemple.fr', type: 'email' },
                    { key: 'telephone', label: 'Téléphone *', placeholder: '06 12 34 56 78', type: 'tel' },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="block text-xs font-medium mb-1" style={{ color: 'var(--ink)' }}>{f.label}</label>
                      <input type={f.type || 'text'} placeholder={f.placeholder}
                        value={clientInfo[f.key]} onChange={e => setClientInfo(p => ({ ...p, [f.key]: e.target.value }))}
                        className="w-full px-3 py-2 rounded-xl text-sm"
                        style={{ background: '#FFFFFF', border: `1px solid ${(f.key === 'email' || f.key === 'telephone') && !clientInfo[f.key] ? 'rgba(229,62,62,0.4)' : 'var(--border)'}`, color: 'var(--ink)' }} />
                    </div>
                  ))}
                </div>

                {/* Adresse de livraison de l'événement */}
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                  {/* Badge livraison offerte */}
                  <div className="flex items-center gap-2 px-3 py-2 rounded-xl mb-3"
                    style={{ background: 'rgba(31,157,122,0.06)', border: '1px solid rgba(31,157,122,0.2)' }}>
                    <Truck size={14} style={{ color: '#1F9D7A', flexShrink: 0 }} />
                    <p className="text-xs font-semibold" style={{ color: '#1F9D7A' }}>
                      🎁 Livraison offerte jusqu'à 20 km autour de Figeac — au-delà, frais selon la zone
                    </p>
                  </div>
                  <p className="font-semibold text-sm mb-2 flex items-center gap-2" style={{ color: 'var(--ink)' }}>
                    📍 Adresse de livraison de l'événement *
                    <span className="text-xs font-normal" style={{ color: 'var(--text-muted)' }}>(peut être différente de votre domicile)</span>
                  </p>
                  {/* Option "même que mon domicile" si profil renseigné */}
                  {user?.adresse_personnelle && (
                    <label className="flex items-center gap-2 mb-2 cursor-pointer">
                      <input type="checkbox" checked={adresseSameChezmoi}
                        onChange={e => setAdresseSameChezmoi(e.target.checked)}
                        className="rounded" />
                      <span className="text-sm" style={{ color: 'var(--ink)' }}>
                        Livrer à mon domicile : <strong>{user.adresse_personnelle}</strong>
                      </span>
                    </label>
                  )}
                  {!adresseSameChezmoi && (
                    <input
                      value={adresseLivraison}
                      onChange={e => setAdresseLivraison(e.target.value)}
                      placeholder="Ex: Salle des fêtes, 12 rue de la Mairie, 46100 Figeac"
                      className="w-full px-3 py-2 rounded-xl text-sm"
                      style={{ background: '#FFFFFF', border: `1px solid ${!adresseLivraison ? 'rgba(229,62,62,0.4)' : 'var(--border)'}`, color: 'var(--ink)' }}
                    />
                  )}
                  <p className="text-xs mt-1.5" style={{ color: 'var(--text-muted)' }}>
                    Indiquez l'adresse exacte où sera utilisé le matériel (salle, domicile, extérieur…)
                  </p>

                  {/* Adresse de facturation (optionnelle) */}
                  <div className="mt-3 pt-3" style={{ borderTop: '1px dashed var(--border)' }}>
                    <label className="flex items-center gap-2 cursor-pointer mb-2">
                      <input type="checkbox" checked={facturationDifferente}
                        onChange={e => setFacturationDifferente(e.target.checked)}
                        className="rounded" />
                      <span className="text-xs font-medium" style={{ color: 'var(--ink)' }}>
                        🧾 Mon adresse de facturation est différente
                      </span>
                    </label>
                    {facturationDifferente && (
                      <input
                        value={adresseFacturation}
                        onChange={e => setAdresseFacturation(e.target.value)}
                        placeholder="Ex: 12 rue du Moulin, 46100 Figeac (domicile du payeur)"
                        className="w-full px-3 py-2 rounded-xl text-sm"
                        style={{ background: '#FFFFFF', border: '1px solid var(--border)', color: 'var(--ink)' }}
                      />
                    )}
                    {!facturationDifferente && (
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                        Adresse de livraison utilisée sur le contrat et la facture.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right — Summary */}
            <div className="lg:col-span-2">
              <div className="sticky top-20 space-y-4">
                {/* Order summary */}
                <div className="rounded-2xl p-5" style={{ background: '#FFFFFF', border: '1px solid var(--border)', boxShadow: '0 8px 32px rgba(26,26,46,0.06)' }}>
                  <p className="font-display font-bold text-base mb-4" style={{ color: 'var(--ink)' }}>Récapitulatif</p>

                  {/* ALERTE DATES MANQUANTES / TROP PROCHES */}
                  {(() => {
                    const alertes = getAlertesDateCart();
                    if (alertes.length === 0) return null;
                    return (
                      <div className="p-3 rounded-xl mb-4" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.25)' }}>
                        <p className="text-xs font-bold mb-1" style={{ color: '#EF4444' }}>⚠️ Attention</p>
                        {alertes.map((a, i) => (
                          <p key={i} className="text-xs" style={{ color: '#DC2626' }}>
                            {a.type === 'no_date'
                              ? `• "${a.product_nom?.slice(0, 25)}" : date de location manquante`
                              : `• "${a.product_nom?.slice(0, 25)}" : dans ${a.diffDays}j — à régler avant l'événement`
                            }
                          </p>
                        ))}
                      </div>
                    );
                  })()}

                  {/* RÈGLE 7J */}
                  <div className="p-3 rounded-xl mb-4 flex items-start gap-2"
                    style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.2)' }}>
                    <Shield size={12} style={{ color: '#C58B19', flexShrink: 0, marginTop: 1 }} />
                    <p className="text-xs" style={{ color: '#C58B19', lineHeight: '1.5' }}>
                      Toute réservation doit être <strong>réglée au moins 7 jours avant</strong> la date de location pour être validée.
                    </p>
                  </div>
                  {walletBalance > 0 && (
                    <div className="flex items-center justify-between p-3 rounded-xl mb-4"
                      style={{ background: 'rgba(31,157,122,0.08)', border: '1px solid rgba(31,157,122,0.2)' }}>
                      <div className="flex items-center gap-2">
                        <Wallet size={14} style={{ color: '#1F9D7A' }} />
                        <p className="text-xs font-semibold" style={{ color: '#1F9D7A' }}>
                          💰 Crédit disponible : <strong>{walletBalance.toFixed(2)}€</strong>
                        </p>
                      </div>
                      <Link to="/creer-evenement?step=devis"
                        className="text-xs font-bold"
                        style={{ color: '#1F9D7A', textDecoration: 'underline' }}>
                        Utiliser →
                      </Link>
                    </div>
                  )}

                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span style={{ color: 'var(--text-muted)' }}>Sous-total animations</span>
                      <span style={{ color: 'var(--ink)' }}>{subtotal.toFixed(2)}€</span>
                    </div>
                    {totalSavings > 0 && profileCfg && (
                      <div className="flex justify-between text-sm items-center">
                        <span className="flex items-center gap-1.5" style={{ color: profileCfg.color }}>
                          <Percent size={12} /> Remise {profileCfg.label} -{profileCfg.pct}%
                        </span>
                        <span className="font-bold" style={{ color: profileCfg.color }}>-{totalSavings.toFixed(2)}€</span>
                      </div>
                    )}
                    {/* ── Réductions tarifaires auto (semaine Lun-Jeu) ── */}
                    {(() => {
                      const locationItems = cartItems.filter(i => i.product?.type === 'location' && i.date_location);
                      const weekdayItems = locationItems.filter(i => {
                        const d = new Date(i.date_location + 'T12:00:00');
                        const day = d.getDay();
                        return day >= 1 && day <= 4;
                      });
                      if (weekdayItems.length === 0) return null;
                      const saving = weekdayItems.reduce((acc, i) => {
                        const prix = i.product?.tarifs?.[clientProfile] || i.product?.tarifs?.particulier || 0;
                        return acc + prix * i.quantite * 0.15;
                      }, 0);
                      return saving > 0 ? (
                        <div className="flex justify-between text-sm items-center">
                          <span className="flex items-center gap-1.5" style={{ color: '#1F9D7A' }}>
                            <Tag size={12} /> Réduction semaine Lun-Jeu -15%
                          </span>
                          <span className="font-bold" style={{ color: '#1F9D7A' }}>-{saving.toFixed(2)}€</span>
                        </div>
                      ) : null;
                    })()}
                    <div className="flex justify-between text-sm">
                      <span style={{ color: 'var(--text-muted)' }}>
                        Transport — 1 forfait
                        <span className="text-xs ml-1" style={{ color: 'var(--text-muted)', opacity: 0.7 }}>
                          ({cartItems.filter(i => i.product?.type === 'location').length} article{cartItems.filter(i => i.product?.type === 'location').length > 1 ? 's' : ''} en loc.)
                        </span>
                      </span>
                      <span className="font-semibold" style={{ color: transport > 0 ? 'var(--ink)' : 'var(--teal)' }}>
                        {transport > 0 ? `+${transport.toFixed(2)}€` : 'Gratuit / À définir'}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs" style={{ borderTop: '1px dashed var(--border)', paddingTop: '8px', marginTop: '4px' }}>
                      <span style={{ color: 'var(--text-muted)' }}>TVA (non applicable)</span>
                      <span style={{ color: 'var(--text-muted)' }}>0.00€</span>
                    </div>
                  </div>

                  <div className="p-3 rounded-xl mb-4" style={{ background: 'var(--teal-mist)', border: '1px solid rgba(93,188,191,0.2)' }}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-bold text-sm" style={{ color: '#0B4B4E' }}>TOTAL TTC</span>
                      <span className="text-2xl font-bold price-gold">{total.toFixed(2)}€</span>
                    </div>

                    {/* CHOIX MODE PAIEMENT */}
                    <p className="text-xs font-bold mb-2" style={{ color: '#0B4B4E' }}>Mode de paiement :</p>

                    {/* Alerte < 7j — paiement total obligatoire */}
                    {hasItemUnder7Days && (
                      <div className="flex items-start gap-2 p-2.5 rounded-xl mb-2"
                        style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}>
                        <AlertCircle size={13} style={{ color: '#EF4444', flexShrink: 0, marginTop: 1 }} />
                        <p className="text-xs font-semibold" style={{ color: '#DC2626', lineHeight: 1.5 }}>
                          Événement dans moins de 7 jours — <strong>paiement total obligatoire</strong>. L'acompte n'est plus possible.
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <button
                        onClick={() => !hasItemUnder7Days && setPaiementMode('acompte')}
                        disabled={hasItemUnder7Days}
                        className="p-2.5 rounded-xl text-xs font-bold text-center transition-all"
                        style={{
                          background: hasItemUnder7Days ? '#F3F4F6' : paiementMode === 'acompte' ? '#0B1E20' : '#FFFFFF',
                          color: hasItemUnder7Days ? '#9CA3AF' : paiementMode === 'acompte' ? '#D4AF37' : '#0B4B4E',
                          border: `1.5px solid ${hasItemUnder7Days ? '#E5E7EB' : paiementMode === 'acompte' ? '#D4AF37' : 'rgba(93,188,191,0.3)'}`,
                          cursor: hasItemUnder7Days ? 'not-allowed' : 'pointer',
                          opacity: hasItemUnder7Days ? 0.5 : 1,
                        }}>
                        Acompte 30%<br />
                        <span style={{ fontSize: 13, fontWeight: 800 }}>{acompte.toFixed(2)}€</span>
                        <br /><span style={{ fontSize: 10, opacity: 0.7 }}>
                          {hasItemUnder7Days ? '⛔ Non disponible' : 'Solde à la livraison'}
                        </span>
                      </button>
                      <button
                        onClick={() => setPaiementMode('total')}
                        className="p-2.5 rounded-xl text-xs font-bold text-center transition-all"
                        style={{
                          background: paiementMode === 'total' ? '#1F9D7A' : '#FFFFFF',
                          color: paiementMode === 'total' ? '#FFFFFF' : '#0B4B4E',
                          border: `1.5px solid ${paiementMode === 'total' ? '#1F9D7A' : 'rgba(93,188,191,0.3)'}`,
                          cursor: 'pointer',
                        }}>
                        Paiement total<br />
                        <span style={{ fontSize: 13, fontWeight: 800 }}>{total.toFixed(2)}€</span>
                        <br /><span style={{ fontSize: 10, opacity: 0.7 }}>Rien à payer le Jour J</span>
                      </button>
                    </div>
                    <p className="text-xs text-center" style={{ color: 'rgba(11,75,78,0.6)' }}>
                      {paiementMode === 'acompte'
                        ? `Acompte aujourd'hui : ${acompte.toFixed(2)}€ · Solde ${solde.toFixed(2)}€ à la livraison`
                        : `Tout régler maintenant : ${total.toFixed(2)}€ · Rien à payer le Jour J`}
                    </p>
                  </div>

                  {/* Savings banner si remise active */}
                  {totalSavings > 0 && profileCfg && (
                    <div className="flex items-center gap-2 p-2.5 rounded-xl mb-3"
                      style={{ background: profileCfg.bg || 'rgba(31,157,122,0.08)', border: `1px solid ${profileCfg.color}25` }}>
                      <Check size={13} style={{ color: profileCfg.color, flexShrink: 0 }} />
                      <p className="text-xs font-semibold" style={{ color: profileCfg.color }}>
                        Vous économisez <strong>{totalSavings.toFixed(2)}€</strong> avec votre tarif {profileCfg.label}
                      </p>
                    </div>
                  )}

                  {/* Return reminder */}
                  <div className="flex items-center gap-2 p-2.5 rounded-xl mb-4"
                    style={{ background: 'rgba(197,139,25,0.06)', border: '1px solid rgba(197,139,25,0.2)' }}>
                    <Clock size={13} style={{ color: '#C58B19' }} />
                    <p className="text-xs font-medium" style={{ color: '#C58B19' }}>
                      Retour : <strong>lendemain avant 10H00</strong>
                    </p>
                  </div>

                  <button onClick={createQuote} disabled={creatingQuote || !clientInfo.email}
                    className="w-full btn-teal py-4 rounded-xl text-base font-bold flex items-center gap-2 justify-center disabled:opacity-40 mb-3">
                    {creatingQuote ? 'Création...' : `${paiementMode === 'acompte' ? `Payer l'acompte ${acompte.toFixed(2)}€` : `Payer ${total.toFixed(2)}€`}`}
                    <ArrowRight size={18} />
                  </button>

                  <div className="flex items-center justify-center gap-2">
                    <Shield size={12} style={{ color: 'var(--text-muted)' }} />
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      Paiement sécurisé Stripe SSL · {paiementMode === 'acompte' ? 'Acompte 30%' : 'Paiement total'}
                    </p>
                  </div>
                </div>

                {/* Trust block */}
                <TrustBlock compact />

                {/* Reviews snippet */}
                <div className="rounded-2xl p-4" style={{ background: '#FFFFFF', border: '1px solid var(--border)' }}>
                  <ReviewsWidget compact maxReviews={2} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <MobileStickyBar />
      <Footer />
    </div>
  );
}
